<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t, locale } = useI18n()
import { useLogger } from "vue-logger-plugin";
const logger = useLogger();

logger.debug('AoeKitCheckout.vue', 'locale', locale)
setStoreLogger(logger)

// Map country code to culture name
function getCultureNameFromCountryCode(countryCode: string): string {
  const cultureMap: Record<string, string> = {
    'US': 'en-US',
    'DE': 'de-DE',
    'AT': 'de-AT',
    'FR': 'fr-FR'
  }
  return cultureMap[countryCode] || 'en-US' // Default to en-US if not found
}

import {
  GetCurrentCartFromLocalStorage,
  GetCurrentCartIdentifiers,
  ShippingAddressAddToCart,
  CartLocalStorageKey,
  SaveCartToLocalStorage,
  GetUniqueProductCodes,
  GuestAddToCart,
  CustomerAddToCart,
  AddItemToCart,
} from '../services/CartService.ts'
import { MarketingOptInCreate, MarketingOptInGet } from '../api/Marketing.ts'
import { CartSubmit, CrossBorderKitCartCreate, CartGet } from '../api/Cart.ts'
import { ConsultantClassCheck } from '../api/Consultant.ts'
import { GetKitCategoryCode } from '../api/Umbraco.ts'
import { ProductByCategoryAndOrderTypeGetList } from '../api/Category.ts'
import {
  Form as kForm,
  FormElement as kformelement,
  type FormSubmitClickEvent,
} from '@progress/kendo-vue-form'
import { IntlProvider, LocalizationProvider } from '@progress/kendo-vue-intl'
import ApplicationField from '../application/ApplicationField.vue'
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { Popup } from '@progress/kendo-vue-popup'
import { Dialog, DialogActionsBar } from '@progress/kendo-vue-dialogs'
import { Checkbox as KCheckBox } from '@progress/kendo-vue-inputs'
import { DropDownList } from '@progress/kendo-vue-dropdowns'
import { DatePicker as KDatePicker } from '@progress/kendo-vue-dateinputs'
import PhoneInputControl from './PhoneInputControl.vue'
import { GetCountriesFromLocalStorageOrApi } from '../services/CountryCultureService'
import { getCountryLabelKey } from '@/config/countries'
import { FormatCurrency } from '../services/currencyFormatter'
import { GetCartProductSubtotal } from '../services/CartTotalsService'
// Removed KDatePicker import as it's now handled by ApplicationFieldDate
import {
  ExpansionPanel,
  ExpansionPanelContent,
} from '@progress/kendo-vue-layout'
import { Reveal } from '@progress/kendo-vue-animation'
import { setStoreLogger } from '../logger.ts'
import { getKendoLanguage, getKendoLocale } from '@/utils/kendoHelper'
import {formatDateForJSONRequest} from '@/api/Common.ts'
import { GetFullProductImageDictionary, GetFullProductUrl } from '../services/SkuService.ts'
import { ref, onMounted, provide } from 'vue'
import type { IAoeCart, IShippingAddress, ICartResponse } from '../types/CartTypes.js'
import type { IAddressVerificationListItem, IAddress } from '../types/CountryCultureTypes.js'
import type { IAoeCustomer } from '../types/CustomerTypes.js'
import {
  GetCartDetailUrl,
  GetOrderTypeId,
  GetRootUrl,
  GetThankYouUrl,
  GetBeeNumberAuthenicatedUser,
  GetCountryCode,
  GetPasswordRegex,
  GetBeeNumberSite,
  GetTermsUrl,
  GetPrivacyPolicyUrl,
  GetConsultantAgreementUrl,
  GetProhibitedPracticesUrl,
  GetCodeOfConductUrl,
  GetReplicatedSiteBusinessName
} from '../stores/SettingsStore.ts'
import { UrlHelpers } from '../utils/urlHelpers.ts'
import { TermsNUWGet } from '../api/ContractAgreement.ts'
import type { ITermsVersionViewModel } from '../types/ContractAgreementTypes'
import {
  trackBeginCheckout,
  trackAddPersonalInfo,
  trackAddShippingInfo,
  getFunnelType,
} from '../analytics/index'
import { buildMatomoItemsFromCart } from '../analytics/matomoItemBuilder'
import { createEmailFieldValidator, createPasswordFormatValidator } from '../validators/StringValidator.ts'
import AoeCheckoutPayment from './AoeCheckoutPayment.vue'
import { AlertCartSubmitResponse, handleError } from '../services/ErrorService.ts'
import VerifyAddressPopup from './VerifyAddressPopup.vue'
import { SetIsLoading } from '../services/AoeHelpers.ts'
import AddressControl from './PWS/AddressControl.vue'
import type { IPhoneOutput } from '../types/CommonTypes'
import { useCookies } from 'vue3-cookies'

const { cookies } = useCookies()

logger.debug('AoeCheckout.vue')

const cart = ref<IAoeCart | null>(null)
const cust = ref<IAoeCustomer | null>(null)
const productImageUrls = ref<Record<string, string>>({})
const orderSummaryItemTitle = ref('')
const addressVerifyError = ref(false)

const fullProductUrlByProductCode = ref<Record<string, string>>({})
const orderTypeId = ref<string>('')
const countryCode = ref('')
const rootUrl = ref<string>('')
const cartDetailUrl = ref<string>('')
const termsUrl = ref<string>('')
const privacyPolicyUrl = ref<string>('')
const consultantAgreementUrl = ref<string>('')
const prohibitedPracticesUrl = ref<string>('')
const codeOfConductUrl = ref<string>('')
const expanded = ref(false)
const step = ref(1)
const show = ref(false)

const firstNameText = ref('')
const lastNameText = ref('')
const emailText = ref('')
const phoneNumberText = ref('')
const phoneModel = ref({
  countryCode: '',
  number: '',
  formattedNumber: '',
  codeIdPhoneType: '',
  extension: ''
})
const phoneControl = ref<InstanceType<typeof PhoneInputControl> | null>(null)
const shippingAddressControl = ref<InstanceType<typeof AddressControl> | null>(null)

// Validation errors for standard inputs
const errors = ref({
  firstName: '',
  lastName: '',
  emailAddress: '',
  password: '',
  confirmPassword: ''
})
const passwordText = ref('')
const confirmPasswordText = ref('')

const birthDate = ref<Date | undefined>(undefined)
const birthDateError = ref('')

function birthDateChangeHandler(e: unknown) {
  const v = (e as { value?: unknown })?.value
  const date = (v instanceof Date && !Number.isNaN(v.getTime()) && v.getFullYear() >= 1000) ? v : undefined
  birthDate.value = date
  logger.debug('AoeKitCheckout', 'birthDateChangeHandler', 'birthDate.value:', birthDate.value)

  // Validate birthday (required + age)
  if (!date) {
    birthDateError.value = t('validators.birthDateRequired')
  } else {
    const today = new Date()
    let age = today.getFullYear() - date.getFullYear()
    const monthDiff = today.getMonth() - date.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
      age--
    }
    if (age < 18) {
      birthDateError.value = t('validators.minimumAge')
    } else {
      birthDateError.value = ''
    }
  }
}

const passwordRegex = ref<string>('')
const showPasswordPattern = ref<boolean>(false)
const certificationAccepted = ref<boolean>(false)
const ethicsAccepted = ref<boolean>(false)
const iceTermsVersionData = ref<ITermsVersionViewModel | null>(null)

// Country dropdown
interface Country {
  text: string
  id: string
}
const countries = ref<Country[]>([])
const selectedCountry = ref<Country>({ text: '', id: '' })

function countryChangeHandler(country: Country) {
  logger.debug('countryChangeHandler', country)
  selectedCountry.value = country
  loadTerms(country.id)

  cartRecreatedForCrossBorder.value = false
}

async function loadTerms(forCountryCode?: string) {
  const code = forCountryCode || countryCode.value
  if (!code) return
  try {
    const iceResponse = await TermsNUWGet(null, code, 'ICE')
    iceTermsVersionData.value = iceResponse?.item ?? null
  } catch (error) {
    logger.error('Failed to load ICE terms for country:', code, error)
    iceTermsVersionData.value = null
  }
}


function handlePasswordChange(...args: any[]): void {
  const e = args[0]
  if (e && typeof e.value === 'string') {
    passwordText.value = e.value
    showPasswordPattern.value = true
    validatePassword()
    validateConfirmPassword()
  }
}

function handleConfirmPasswordChange(...args: any[]): void {
  const e = args[0]
  if (e && typeof e.value === 'string') {
    confirmPasswordText.value = e.value
    validateConfirmPassword()
  }
}

function handleEmailChange(...args: any[]): void {
  const e = args[0]
  if (e && typeof e.value === 'string') {
    emailText.value = e.value
    checkConsultantEmail(e.value)
  }
}
const addressFirstNameText = ref('')
const addressLastNameText = ref('')
const addressLine1Text = ref('')
const addressLine2Text = ref<string | null>('')
const houseNumberText = ref<string | null>('')
const cityText = ref('')
const countryText = ref('')
const stateText = ref<string | null>('')
const postalCodeText = ref('')

// Non-reactive display ref for AddressControl name field to prevent space insertion bug
// See TEAU-969 fixes: d0783b0, 605ffb0
const addressNameDisplay = ref('')
const myForm = ref(null)
const mySecondForm = ref(null)
const verifyAddress = ref<IAddressVerificationListItem[]>([])
const isEditStep = ref(false)
const isAddressChange = ref(false)
const alreadyOptedin = ref(false)
const isAuthenticatedUser = ref(false)
const consultantEmailError = ref<string | null>(null)
const originalKitOrderTypeId = ref<string>('')
const originalKitCategoryCode = ref<string>('')
const originalKitSkuCode = ref<string>('')
const showSkuSelectionDialog = ref(false)
const availableKitProducts = ref<any[]>([])
const selectedKitSku = ref<string>('')
const pendingShippingAddress = ref<IShippingAddress | null>(null)
const cartRecreatedForCrossBorder = ref(false)
const hasTrackedBeginCheckout = ref(false)
const isTrackingBeginCheckout = ref(false)
const hasTrackedAnyPersonalInfo = ref(false)
const hasTrackedAutofillShippingInfo = ref(false)
const lastTrackedPersonalInfoSnapshot = ref('')

onMounted(async () => {
  logger.debug('onMounted')
  const urlParams = new URLSearchParams(window.location.search)
  const qsStep = urlParams.get('step')
  logger.debug('qsStep:', qsStep)
  if (qsStep) {
    step.value = parseInt(qsStep)
  }

  countryCode.value = await GetCountryCode()
  countryText.value = countryCode.value // Initialize countryText with default country code
  phoneModel.value.countryCode = countryCode.value // Initialize phone country code

  // Store original kit order type ID and SKU if this is a kit cart
  const kitCart: IAoeCart | null = GetCurrentCartFromLocalStorage()
  if (kitCart && kitCart.items && kitCart.items.length > 0) {
    // Check if this is a kit order by looking at the cart's order type ID
    const urlParams = new URLSearchParams(window.location.search)
    const kitOrderTypeIdParam = urlParams.get('kitOrderTypeId')
    const kitCategoryCodeParam = urlParams.get('kitCategoryCode')

    if (kitOrderTypeIdParam) {
      originalKitOrderTypeId.value = kitOrderTypeIdParam
      originalKitCategoryCode.value = kitCategoryCodeParam || ''
      originalKitSkuCode.value = kitCart.items[0].skuCode
      logger.debug('Kit cart detected:', { originalKitOrderTypeId: originalKitOrderTypeId.value, originalKitSkuCode: originalKitSkuCode.value })
    }
  }

  // Load countries for dropdown
  GetCountriesFromLocalStorageOrApi().then((data) => {
    if (data?.items) {
      for (const country of data?.items) {
        const translatedName = t(getCountryLabelKey(country.countryCode))
        countries.value.push({ text: translatedName, id: country.countryCode })

        if (country.countryCode === countryCode.value) {
          selectedCountry.value = { text: translatedName, id: country.countryCode }
        }
      }

      countries.value.sort((a, b) => {
        return a.text.localeCompare(b.text)
      })
    }
  })

  orderTypeId.value = await GetOrderTypeId()
  rootUrl.value = await GetRootUrl()
  cartDetailUrl.value = await GetCartDetailUrl()
  passwordRegex.value = await GetPasswordRegex()
  const businessName = await GetReplicatedSiteBusinessName()
  // TermsUrl / PrivacyPolicyUrl are absolute settings URLs that skip the server-side
  // replicated-site rewrite, so re-apply it here to keep the user on /pws/{business}/.
  termsUrl.value = UrlHelpers.toReplicatedSiteUrl(await GetTermsUrl(), businessName)
  privacyPolicyUrl.value = UrlHelpers.toReplicatedSiteUrl(await GetPrivacyPolicyUrl(), businessName)
  consultantAgreementUrl.value = await GetConsultantAgreementUrl()
  prohibitedPracticesUrl.value = await GetProhibitedPracticesUrl()
  codeOfConductUrl.value = await GetCodeOfConductUrl()
  await loadTerms()
  const tempCart: IAoeCart | null = GetCurrentCartFromLocalStorage()
  if (tempCart) {
    const uniqueProductCodes = GetUniqueProductCodes(tempCart)
    logger.debug('uniqueProductCodes=', uniqueProductCodes)
    productImageUrls.value =
      await GetFullProductImageDictionary(uniqueProductCodes)
    logger.debug('productImageUrls', productImageUrls)
  }

  if (tempCart) await UpdateFullProductUrlDiscountedItems(tempCart)
  cart.value = tempCart

  //check if customer logged in, then check if cart has customer, if not add customer to cart
  const beeNumberAuthenicatedUser = await GetBeeNumberAuthenicatedUser()
  if (cart.value && beeNumberAuthenicatedUser) {
    if (cart.value.beeBusinessGuidCustomer == null || cart.value.beeBusinessGuidCustomer == undefined) {
      await CustomerAddToCart({
        aoeCartIdToken: cart.value.aoeCartIdToken,
        beeNumberCustomer: beeNumberAuthenicatedUser,
        autoApplyKitCoupon: true
      });
    }
  }

  await trackBeginCheckoutOnce(tempCart)

  if (beeNumberAuthenicatedUser) {
    isAuthenticatedUser.value = true
    cust.value = {
      emailAddress: cart.value?.customerEmailAddress,
      firstName: cart.value?.customerFirstName,
      lastName: cart.value?.customerLastName,
      phone: cart.value?.customerPhoneNumber,
    } as IAoeCustomer
  }
  else
  {
    cust.value = {
      emailAddress: cart.value?.guestEmailAddress,
      firstName: cart.value?.guestFirstName,
      lastName: cart.value?.guestLastName,
      phone: cart.value?.guestPhoneNumber,
    } as IAoeCustomer

  }
  const response = await MarketingOptInGet({ emailAddress: cust.value.emailAddress })
  alreadyOptedin.value =  response.initialOptIn != null


  if (cust.value != null && cust.value.emailAddress != undefined)
    {
      formInitialValues.value = cust.value
      disableEmail.value = true

      firstNameText.value = cust.value.firstName
      lastNameText.value = cust.value.lastName
      emailText.value = cust.value.emailAddress
      phoneNumberText.value = cust.value.phone || ''

      await trackPersonalInfoAutofillOnce()
      moveToNextStep()
    }

  if (cart.value?.shippingAddress != null)
  {
    secondFormInitialValues.value = cart.value.shippingAddress
    addressFirstNameText.value = cart.value.shippingAddress.firstName
    addressLastNameText.value = cart.value.shippingAddress.lastName
    addressNameDisplay.value = (cart.value.shippingAddress.firstName || '') + ' ' + (cart.value.shippingAddress.lastName || '')
    addressLine1Text.value = cart.value.shippingAddress.addressLine1
    addressLine2Text.value = cart.value.shippingAddress.addressLine2
    houseNumberText.value = cart.value.shippingAddress.houseNumber
    cityText.value = cart.value.shippingAddress.city
    stateText.value = cart.value.shippingAddress.stateCode
    postalCodeText.value = cart.value.shippingAddress.postalCode
    countryText.value = cart.value.shippingAddress.countryCode

    await trackShippingInfoAutofillOnce()
  }


  orderSummaryItemTitle.value = t('checkout.OrderSummaryYourOrder')
  if (cart.value) {
    let totalQty = 0
    for (const item of cart.value.items) {
      totalQty += item.quantity
    }

    orderSummaryItemTitle.value += '  (' + totalQty + ' ' + t('checkout.OrderSummaryItem') +')'
  }

  handleAction()
})

function handleAction() {
  expanded.value = !expanded.value
}

async function trackBeginCheckoutOnce(tempCart: IAoeCart | null) {
  if (!tempCart || tempCart.items.length === 0 || hasTrackedBeginCheckout.value || isTrackingBeginCheckout.value) {
    return
  }

  isTrackingBeginCheckout.value = true
  hasTrackedBeginCheckout.value = true

  try {
    const matomoItems = await buildMatomoItemsFromCart(tempCart, orderTypeId.value)
    trackBeginCheckout(matomoItems, tempCart.total ?? 0, tempCart.currencyId || 'EUR', 'recruitment', tempCart.couponCode)
  } finally {
    isTrackingBeginCheckout.value = false
  }
}

async function trackPersonalInfoManual() {
  await trackPersonalInfo('manual')
}

async function trackShippingInfoManual() {
  await trackBeginCheckoutOnce(cart.value)
  if (!hasTrackedAnyPersonalInfo.value) {
    await trackPersonalInfoAutofillOnce()
  }
  trackAddShippingInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    'recruitment'
  )
}

async function trackShippingInfoAutofillOnce() {
  await trackBeginCheckoutOnce(cart.value)
  if (!hasTrackedAnyPersonalInfo.value) {
    await trackPersonalInfoAutofillOnce()
  }
  if (hasTrackedAutofillShippingInfo.value) {
    return
  }

  hasTrackedAutofillShippingInfo.value = true
  trackAddShippingInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    'recruitment'
  )
}

async function UpdateFullProductUrlDiscountedItems(cart: IAoeCart) {
  logger.debug('UpdateFullProductUrlDiscountedItems')
  for (const cartItem of cart.items.filter(
    item =>
      !item.poeDiscPlanLevelDiscIds ||
      item.poeDiscPlanLevelDiscIds.length === 0,
  )) {
    let fullProductUrl = fullProductUrlByProductCode.value[cartItem.productCode]
    if (fullProductUrl) continue
    fullProductUrl = await GetFullProductUrl(
      cartItem.categoryCode,
      cartItem.productCode,
    )
    fullProductUrlByProductCode.value[cartItem.productCode] = fullProductUrl
  }
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug(
    'GetFullProductImageUrlFromStringDictionary',
    productCode,
    productImageUrls,
  )
  const productImageUrl = productImageUrls.value[productCode]
  logger.debug('productImageUrl=', productImageUrl)
  if (productImageUrl) return productImageUrl
  return '#'
}

function GetFullProductUrlFromStringDictionary(productCode: string): string {
  const fullProductUrl = fullProductUrlByProductCode.value[productCode]
  if (fullProductUrl) {
    logger.debug('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  } else {
    console.warn('productCode=', productCode, 'fullProductUrl=', fullProductUrl)
  }
  return fullProductUrl || '#'
}

window.addEventListener('storage', storageHandler, false)
async function storageHandler(event: StorageEvent) {
  logger.debug('storageHandler', event)
  const cartIdentifiers = GetCurrentCartIdentifiers()
  if (cartIdentifiers && event.key === CartLocalStorageKey) {
    cart.value = GetCurrentCartFromLocalStorage()

    if (cart.value?.customerFirstName != undefined)
    {
      cust.value = {
        emailAddress: cart.value?.customerEmailAddress,
        firstName: cart.value?.customerFirstName,
        lastName: cart.value?.customerLastName,
        phone: cart.value?.customerPhoneNumber,
      } as IAoeCustomer
    }
    else if (cart.value?.guestFirstName != undefined)
    {
      cust.value = {
        emailAddress: cart.value?.guestEmailAddress,
        firstName: cart.value?.guestFirstName,
        lastName: cart.value?.guestLastName,
        phone: cart.value?.guestPhoneNumber,
      } as IAoeCustomer
    }

    if (cust.value != null)
      {
        formInitialValues.value = cust.value
        disableEmail.value = true

        firstNameText.value = cust.value.firstName
        lastNameText.value = cust.value.lastName
        emailText.value = cust.value.emailAddress
        phoneNumberText.value = cust.value.phone || ''

        await trackPersonalInfoAutofillOnce()
      }

    if (cart.value?.shippingAddress != null && secondFormInitialValues.value.addressLine1 == undefined)
    {
      secondFormInitialValues.value = cart.value.shippingAddress
      addressFirstNameText.value = cart.value.shippingAddress.firstName
      addressLastNameText.value = cart.value.shippingAddress.lastName
      addressNameDisplay.value = (cart.value.shippingAddress.firstName || '') + ' ' + (cart.value.shippingAddress.lastName || '')
      addressLine1Text.value = cart.value.shippingAddress.addressLine1
      addressLine2Text.value = cart.value.shippingAddress.addressLine2
      houseNumberText.value = cart.value.shippingAddress.houseNumber
      cityText.value = cart.value.shippingAddress.city
      stateText.value = cart.value.shippingAddress.stateCode
      postalCodeText.value = cart.value.shippingAddress.postalCode

      await trackShippingInfoAutofillOnce()

      moveToNextStep()
    }
  }
}

const formInitialValues = ref<IAoeCustomer>({
  emailAddress: '',
  firstName: '',
  lastName: '',
  phone: '',
  birthDate: undefined,
  password: '',
  confirmPassword: '',
  beeNumber: 0,
  reinstatementEligible: false
} as IAoeCustomer)
const secondFormInitialValues = ref<IShippingAddress>({ } as IShippingAddress)
const disableEmail = ref(false)
const marketingOptIn = ref(false)
const hasTrackedAutofillPersonalInfo = ref(false)

function buildPersonalInfoSnapshot(): string {
  return JSON.stringify({
    firstName: firstNameText.value?.trim() ?? '',
    lastName: lastNameText.value?.trim() ?? '',
    emailAddress: emailText.value?.trim().toLowerCase() ?? '',
    phone: phoneNumberText.value?.trim() ?? '',
  })
}

async function trackPersonalInfo(source: 'manual' | 'autofill') {
  await trackBeginCheckoutOnce(cart.value)

  const currentSnapshot = buildPersonalInfoSnapshot()
  const isDuplicateSnapshot = currentSnapshot === lastTrackedPersonalInfoSnapshot.value

  if (source === 'autofill' && hasTrackedAutofillPersonalInfo.value) {
    return
  }

  if (isDuplicateSnapshot) {
    if (source === 'autofill') {
      hasTrackedAutofillPersonalInfo.value = true
    }
    return
  }

  trackAddPersonalInfo(
    cart.value?.total ?? 0,
    cart.value?.currencyId || 'EUR',
    'recruitment'
  )
  hasTrackedAnyPersonalInfo.value = true
  lastTrackedPersonalInfoSnapshot.value = currentSnapshot

  if (source === 'autofill') {
    hasTrackedAutofillPersonalInfo.value = true
  }
}

async function trackPersonalInfoAutofillOnce() {
  await trackPersonalInfo('autofill')
}

async function handleMarketingOptIn(emailAddress: string) {
  try {
    if (marketingOptIn.value) {
      await MarketingOptInCreate({
        emailAddress: emailAddress,
        dataUsage: 'M'
      })
      logger.debug('Marketing opt-in created successfully')
    }
  } catch (error) {
    console.error('Error creating marketing opt-in:', error)
    await handleError(error, t)
  }
}

const formProperties = {
  mode: 'create',
} as ApplicationEditFormProperties

provide('ApplicationEditFormProperties', formProperties)

function moveToNextStep(){
  logger.debug('moveToNextStep')

  if (step.value == 1)
  {
    if (cart.value?.shippingAddress != null)
    {
      step.value = 3
    }
    else
    {
      step.value = 2
    }
  }
  else if (step.value == 2)
  {
    if (secondFormInitialValues.value != null)
    {
      step.value = 3
    }
  }

}

function editStep1ClickHandler() {
  logger.debug('editStep1ClickHandler')
  birthDate.value = undefined
  step.value = 1
}

function editStep2ClickHandler() {
  logger.debug('editStep2ClickHandler')
  step.value = 2
  isEditStep.value = true
}

function onSubmitClickHandler(event: FormSubmitClickEvent) {
  logger.debug('event:', event)
  performSubmit(event)
}

async function handleContinue1Click() {
  logger.debug('handleContinue1Click')

  // Validate standard inputs
  validateFirstName()
  validateLastName()
  validateEmailAddress()
  if (!isAuthenticatedUser.value) {
    validatePassword()
    validateConfirmPassword()
  }

  // Validate phone control
  if (phoneControl.value && !phoneControl.value.validate()) {
    return
  }

  // Validate birthday
  if (!birthDate.value) {
    birthDateError.value = t('validators.birthDateRequired')
  } else {
    const today = new Date()
    let age = today.getFullYear() - birthDate.value.getFullYear()
    const monthDiff = today.getMonth() - birthDate.value.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.value.getDate())) {
      age--
    }
    if (age < 18) {
      birthDateError.value = t('validators.minimumAge')
    } else {
      birthDateError.value = ''
    }
  }

  if (hasStepOneValidationErrors()) {
    return
  }

  phoneNumberText.value = phoneModel.value.formattedNumber || phoneModel.value.number

  const success = await AddGuestToCart(emailText.value, firstNameText.value, lastNameText.value, phoneNumberText.value, birthDate.value)

  if (success) {
    moveToNextStep()
    await trackPersonalInfoManual()
  }
}

async function performSubmit(event: FormSubmitClickEvent) {
  logger.debug('event:', event)

  if (!isEditStep.value && (event.isModified == false && (event.event.submitter.id === 'btnContinue2' && !isAddressChange.value) ) && formProperties.mode === 'create') {
    logger.debug('Form is not modified')
    return
  }
  else if (isEditStep.value && event.isModified == false && formProperties.mode === 'create' && event.event.submitter.id === 'btnContinue2' && !isAddressChange.value) {
    logger.debug('Form is not modified, edit step')
    // Still run validation so the user sees required field messages
    const shippingValid = shippingAddressControl.value?.validate() ?? true
    if (!shippingValid) {
      return
    }
    moveToNextStep();
    return
  }
  else if (event.isModified == false && formProperties.mode === 'update') {
    logger.debug('Form is not modified')
    return
  } else if (event.isValid === false) {
    logger.debug('Form is not valid')
    return
  } else {
    if (event.event.submitter.id === 'btnContinue1') {
      logger.debug('btnContinue1')
      logger.debug('All form values:', event.values)

      // Validate phone control
      if (phoneControl.value && !phoneControl.value.validate()) {
        return
      }

      // Validate standard inputs
      passwordText.value = event.values.password
      confirmPasswordText.value = event.values.confirmPassword
      validateFirstName()
      validateLastName()
      validateEmailAddress()
      if (!isAuthenticatedUser.value) {
        validatePassword()
        validateConfirmPassword()
      }

      // Validate birthday
      if (!birthDate.value) {
        birthDateError.value = t('validators.birthDateRequired')
      } else {
        const today = new Date()
        let age = today.getFullYear() - birthDate.value.getFullYear()
        const monthDiff = today.getMonth() - birthDate.value.getMonth()
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.value.getDate())) {
          age--
        }
        if (age < 18) {
          birthDateError.value = t('validators.minimumAge')
        } else {
          birthDateError.value = ''
        }
      }

      if (hasStepOneValidationErrors()) {
        return
      }

      phoneNumberText.value = phoneModel.value.formattedNumber || phoneModel.value.number
      passwordText.value = event.values.password
      confirmPasswordText.value = event.values.confirmPassword

      const success = await AddGuestToCart(emailText.value, firstNameText.value, lastNameText.value, phoneNumberText.value, birthDate.value)

      if (success) {
        moveToNextStep()
        await trackPersonalInfoManual()
      }
    } else if (event.event.submitter.id === 'btnContinue2') {
      logger.debug('btnContinue2')

      // Validate shipping address fields
      const shippingValid = shippingAddressControl.value?.validate() ?? true

      if (!shippingValid) {
        return
      }

      addShippingAddressToCart(false)
    }
  }
}

async function AddGuestToCart(emailAddress: string, firstName: string, lastName: string, phone: string, birthDate: Date | undefined): Promise<boolean>
{
  logger.debug('AddGuestToCart', 'emailAddress:', emailAddress, 'firstName:', firstName, 'lastName:', lastName, 'phone:', phone, 'birthDate:', birthDate)
  if (cart.value && cart.value.beeNumberCustomer) {
    return true;
  }

  if (cart.value) {
    try {
      // Validate that birthdate is set
      if (!birthDate) {
        logger.error('Birth date is required but not set')
        alert(t('consultantCorner.addContact.requiredFieldsError'))
        return false
      }

      const selectedCountryCode = selectedCountry.value.id || countryCode.value

       if (cart.value && (cart.value.beeNumberCustomer == null || cart.value.beeNumberCustomer == undefined)) {
          try {
            const guestCartData = {
              aoeCartIdToken: cart.value.aoeCartIdToken,
              emailAddress: emailAddress,
              firstName: firstName,
              lastName: lastName,
              phoneNumber: phone,
              customerAttributes: {
                primaryPhonePhoneNumber: phone,
                primaryPhoneCountryCode: selectedCountryCode,
                primaryPhoneNumber: phone,
                primaryPhoneCodeIdPhoneType: 'HOME',
                password: passwordText.value,
                confirmPassword: confirmPasswordText.value,
                birthDate: formatDateForJSONRequest(birthDate!),
                countryCode: selectedCountryCode,
                cultureName: getCultureNameFromCountryCode(selectedCountryCode),
                webAccount: 'true'
              },
              autoApplyKitCoupon: true
            };
            await GuestAddToCart(guestCartData);

          } catch (error) {
            console.error('Error in GuestAddToCart:', error);
            await handleError(error, t)
          }
       }
      return true;
    } catch (error) {
      console.error('Error in AddGuestToCart:', error);
      await handleError(error, t)
      return false;
    }
  }
  return false;
}

function useEnteredAddressClick() {
  logger.debug('useEnteredAddressClick')

  // Validate shipping address fields before proceeding
  const shippingValid = shippingAddressControl.value?.validate() ?? true
  if (!shippingValid) {
    SetIsLoading(false)
    return
  }

  SetIsLoading(true)
  addShippingAddressToCart(true)
}

function useSelectedAddressClick(selectedAddress: IAddress) {
  logger.debug('useSelectedAddressClick')
  SetIsLoading(true)
    if(selectedAddress != undefined)
    {
      verifyAddress.value = []
      addressLine1Text.value = selectedAddress.addressLine1
      addressLine2Text.value = selectedAddress.addressLine2
      houseNumberText.value = selectedAddress.houseNumber
      cityText.value = selectedAddress.city
      stateText.value = selectedAddress.stateCode
      postalCodeText.value = selectedAddress.postalCode

      logger.debug('selectedAddress:', selectedAddress)

      addShippingAddressToCart(false)
    }
}

async function addShippingAddressToCart(skipAddressValidation: boolean)
{
  show.value = false;
  verifyAddress.value = []
  logger.debug('addShippingAddressToCart')
  const shippingAddress: IShippingAddress = {
        firstName: addressFirstNameText.value,
        lastName: addressLastNameText.value,
        addressLine1: addressLine1Text.value,
        addressLine2: addressLine2Text.value,
        houseNumber: houseNumberText.value,
        city: cityText.value,
        stateCode: stateText.value,
        postalCode: postalCodeText.value,
        countryCode: countryText.value,
      }

  // Check if this is a kit cart and if the address country differs from the site country
  if (originalKitOrderTypeId.value && countryText.value !== countryCode.value && !cartRecreatedForCrossBorder.value) {
    logger.debug('Country mismatch detected for kit cart - initial cart recreation', { addressCountry: countryText.value, siteCountry: countryCode.value })

    // Recreate cart with correct country-specific order type and SKU (first time only)
    const recreated = await recreateCartWithCorrectCountrySku(shippingAddress)
    if (!recreated) {
      SetIsLoading(false)
      return
    }

    // Mark that we've recreated the cart for cross-border
    cartRecreatedForCrossBorder.value = true
  } else {
    //Call Cart Address Add
    logger.debug('ShippingAddressAddToCart')
    await ShippingAddressAddToCart({
      aoeCartIdToken: cart.value != null ? cart.value.aoeCartIdToken : '',
      address: shippingAddress,
      skipAddressValidation: skipAddressValidation,
    }).then(response => {
      logger.debug('ShippingAddressAddToCart response:', response)
      for (const item of response.items) {
        verifyAddress.value.push(item);
      }

      if (response.item == null)
      {
        addressVerifyError.value = true
      }
      else
      {
        addressVerifyError.value = false
      }
    })
  }

  if (verifyAddress.value.length == 0 && addressVerifyError.value == false) {
    secondFormInitialValues.value = shippingAddress
    moveToNextStep()
    await trackShippingInfoManual()
  }
  else
  {
    show.value = true;
  }

  SetIsLoading(false)
}

async function recreateCartWithCorrectCountrySku(shippingAddress: IShippingAddress): Promise<boolean> {
  try {
    logger.debug('Recreating cart with correct country-specific SKU using CrossBorderKitCartCreate')

    // Get current cart token
    const currentCartToken = cart.value?.aoeCartIdToken || cookies.get('AoeCartIdToken')
    if (!currentCartToken) {
      logger.error('No cart token found')
      return false
    }

    // Get the kit category code for the new country
    const kitCategoryCode = await GetKitCategoryCode(shippingAddress.countryCode)
    logger.debug('Kit category code for country:', { countryCode: shippingAddress.countryCode, kitCategoryCode })

    // Call the consolidated API that handles cart creation, guest addition, and shipping address
    const response = await CrossBorderKitCartCreate({
      aoeCartIdToken: currentCartToken,
      address: shippingAddress
    })

    logger.debug('CrossBorderKitCartCreate response:', response)

    // Update cookie to point to the new cart token
    cookies.set('AoeCartIdToken', response.aoeCartIdToken)


    cartRecreatedForCrossBorder.value = true

    // Fetch the newly created cart and update local reference
    const newCartResponse = await CartGet(response.aoeCartIdToken)
    logger.debug('New cart details:', newCartResponse)

    // Update local cart reference with the new cart
    cart.value = GetCurrentCartFromLocalStorage()

    // Handle address verification results - store them but don't block SKU selection
    if (response.validAddresses && response.validAddresses.length > 0) {
      verifyAddress.value = [] // Clear previous validation results
      for (const item of response.validAddresses) {
        verifyAddress.value.push(item)
      }
    }

    // Set address validation flags but continue with SKU selection
    if (response.item == null) {
      logger.debug('Address validation issue detected, will show popup after SKU selection:', response.addressValidationMessageId)
      addressVerifyError.value = true
    } else {
      addressVerifyError.value = false
    }

    // Always proceed with SKU selection as long as we have a valid cart token
    if (!newCartResponse?.item?.orderTypeId) {
      logger.error('Could not retrieve order type from new cart')
      await handleError(new Error(t('kit.noProductsAvailable')), t)
      return false
    }

    const orderTypeId = newCartResponse.item.orderTypeId
    logger.debug('Using order type ID from new cart:', orderTypeId)

    // Fetch available kit products for the new country's category and order type
    const productsResponse = await ProductByCategoryAndOrderTypeGetList({
      orderTypeId: orderTypeId,
      categoryCode: kitCategoryCode,
      includeInventoryLevels: true,
      beeNumber: null
    })

    logger.debug('Available kit products:', productsResponse)

    if (!productsResponse?.item?.products || productsResponse.item.products.length === 0) {
      logger.error('No kit products available for this country')
      await handleError(new Error(t('kit.noProductsAvailable')), t)
      return false
    }

    const products = productsResponse.item.products
    const availableSkus = products.flatMap(p => p.skus || []).filter(sku =>
      sku.outOfStockVisible === true ||
      (sku.inventoryLevel?.inventoryLevel !== 'OutOfStock' && sku.inventoryLevel?.inventoryLevel !== 'StopSell')
    )

    if (availableSkus.length === 0) {
      logger.error('No available SKUs for this country')
      await handleError(new Error(t('kit.noProductsAvailable')), t)
      return false
    }

    let selectedSku: string

    if (availableSkus.length === 1) {
      // Auto-select the only available SKU
      selectedSku = availableSkus[0].skuCode
      logger.debug('Auto-selecting single available SKU:', selectedSku)
    } else {
      // Multiple SKUs available - show selection dialog
      logger.debug('Multiple SKUs available, showing selection dialog')
      availableKitProducts.value = products
      pendingShippingAddress.value = shippingAddress
      showSkuSelectionDialog.value = true

      // Return false to prevent moving to next step until user selects
      return false
    }

    // Add the selected SKU to the new cart
    const beeNumberPayee = await GetBeeNumberSite()
    const beeNumberCustomer = await GetBeeNumberAuthenicatedUser()

    await AddItemToCart(
      response.aoeCartIdToken,
      beeNumberPayee,
      beeNumberCustomer,
      orderTypeId,
      selectedSku,
      1
    )

    // Fetch the updated cart from API to ensure we have the latest data
    const updatedCartResponse = await CartGet(response.aoeCartIdToken)
    logger.debug('Updated cart after SKU addition:', updatedCartResponse)

    // Update local cart reference with the latest cart data
    cart.value = GetCurrentCartFromLocalStorage()

    // Update product images for new SKU
    if (cart.value) {
      const uniqueProductCodes = GetUniqueProductCodes(cart.value)
      productImageUrls.value = await GetFullProductImageDictionary(uniqueProductCodes)
      await UpdateFullProductUrlDiscountedItems(cart.value)
    }

    logger.debug('Cart successfully recreated with correct country SKU')
    return true

  } catch (error) {
    logger.error('Error recreating cart with correct country SKU:', error)
    await handleError(error, t)
    return false
  }
}

async function handleSkuSelection(skuCode: string) {
  try {
    logger.debug('User selected SKU:', skuCode)
    selectedKitSku.value = skuCode
    showSkuSelectionDialog.value = false

    if (!pendingShippingAddress.value) {
      logger.error('No pending shipping address found')
      return
    }

    SetIsLoading(true)

    // Get the new cart token from cookies (set by CrossBorderKitCartCreate)
    const newCartToken = cookies.get('AoeCartIdToken')
    if (!newCartToken) {
      logger.error('No cart token found after cart creation')
      SetIsLoading(false)
      return
    }

    // Get the correct order type ID
    const cartResponse = await CartGet(newCartToken)
    const orderTypeId = cartResponse.item?.orderTypeId || originalKitOrderTypeId.value

    // Add the selected SKU to the new cart
    const beeNumberPayee = await GetBeeNumberSite()
    const beeNumberCustomer = await GetBeeNumberAuthenicatedUser()

    await AddItemToCart(
      newCartToken,
      beeNumberPayee,
      beeNumberCustomer,
      orderTypeId,
      skuCode,
      1
    )

    // Fetch the updated cart from API to ensure we have the latest data
    const updatedCartResponse = await CartGet(newCartToken)
    logger.debug('Updated cart after manual SKU selection:', updatedCartResponse)

    // Update local cart reference with the latest cart data
    cart.value = GetCurrentCartFromLocalStorage()

    // Update product images for new SKU
    if (cart.value) {
      const uniqueProductCodes = GetUniqueProductCodes(cart.value)
      productImageUrls.value = await GetFullProductImageDictionary(uniqueProductCodes)
      await UpdateFullProductUrlDiscountedItems(cart.value)
    }

    // Move to next step if no address verification needed, otherwise ask the shopper to confirm the
    // address. This is the deferred half of the cross border flow: the address popup is held back
    // until a kit has been chosen, so without the else branch an address needing confirmation ends
    // the flow silently, with neither the next step nor the popup.
    if (verifyAddress.value.length == 0 && addressVerifyError.value == false) {
      if (pendingShippingAddress.value) {
        secondFormInitialValues.value = pendingShippingAddress.value
      }
      moveToNextStep()
      await trackShippingInfoManual()
    }
    else {
      show.value = true
    }

    // Clear pending address
    pendingShippingAddress.value = null

    SetIsLoading(false)
  } catch (error) {
    logger.error('Error adding selected SKU to cart:', error)
    await handleError(error, t)
    SetIsLoading(false)
  }
}

function closeSkuSelectionDialog() {
  showSkuSelectionDialog.value = false
  pendingShippingAddress.value = null
  SetIsLoading(false)
}

function getInventoryLevelText(inventoryLevel: string): string {
  const levelMap: Record<string, string> = {
    'Available': t('partyOrderEntry.Available'),
    'LowStock': t('partyOrderEntry.LowStock'),
    'OutOfStock': t('partyOrderEntry.OutOfStock'),
    'StopSell': t('partyOrderEntry.StopSell')
  }
  return levelMap[inventoryLevel] || inventoryLevel
}

function getStockClass(inventoryLevel: { inventoryLevel: string }) {
  const level = inventoryLevel.inventoryLevel.toLowerCase()
  return {
    'stock-available': level === 'available',
    'stock-low': level === 'lowstock',
    'stock-out': level === 'outofstock' || level === 'stopsell'
  }
}

function addressControlUpdateHandler(data: IAddress)
{
  logger.debug('addressControlUpdateHandler', data)
  addressFirstNameText.value = data.firstName != null ? data.firstName : ''
  addressLastNameText.value = data.lastName != null ? data.lastName : ''
  addressLine1Text.value = data.addressLine1
  addressLine2Text.value = data.addressLine2 != null ? data.addressLine2 : ''
  houseNumberText.value = data.houseNumber != null ? data.houseNumber : ''
  cityText.value = data.city
  stateText.value = data.stateCode
  postalCodeText.value = data.postalCode
  countryText.value = data.countryCode
  isAddressChange.value = true
}

async function placeOrderClickHandler() {
  logger.debug('placeOrderClickHandler',cart.value)
  logger.debug('placeOrderClickHandler',marketingOptIn.value, cust.value?.emailAddress)
  if (cart.value) {
    if(marketingOptIn.value && (cust.value?.emailAddress || emailText.value))
    {
      await handleMarketingOptIn(cust.value?.emailAddress || emailText.value)
    }
    SetIsLoading(true)
    CartSubmit(cart.value?.aoeCartIdToken).then(response =>
    {
      logger.debug('response:', response)
      if (response.aoeOrderId)
      {
        // Update ReplicatedSiteBeeNumber cookie with the upgraded consultant's BeeNumber
        if (response.item?.beeNumberCustomer) {
          cookies.set('ReplicatedSiteBeeNumber', response.item.beeNumberCustomer.toString())
          logger.debug('Updated ReplicatedSiteBeeNumber cookie to:', response.item.beeNumberCustomer)
        }

        GetThankYouUrl().then(url =>
        {
          window.location.href = url + "?aoeOrderIdToken=" + response.aoeOrderIdToken
        })
      }
      else
      {
        SetIsLoading(false)
        SaveCartToLocalStorage(response as ICartResponse)
        AlertCartSubmitResponse(t, response.item)
      }
    })
    .catch(async error => {
      SetIsLoading(false)
      await handleError(error, t)
    })
  }
}


// Function to check if email belongs to a consultant
async function checkConsultantEmail(emailAddress: string) {
  if (!emailAddress) {
    consultantEmailError.value = null
    return
  }

  // First validate email format
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  if (!emailRegex.test(emailAddress)) {
    consultantEmailError.value = null
    return
  }

  try {
    const response = await ConsultantClassCheck(emailAddress)
    if (response.isConsultant) {
      consultantEmailError.value = t('validators.consultantEmailNotAllowed')
    } else {
      consultantEmailError.value = null
    }
  } catch (error) {
    logger.error('Error checking consultant email:', error)
    // Don't block the form if the API call fails
    consultantEmailError.value = null
  }
}

// Sync validator that checks the reactive ref
const consultantEmailValidator = (value: string) => {
  return consultantEmailError.value || ''
}

// Validation functions for standard inputs
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  return emailRegex.test(email)
}

const validateFirstName = () => {
  if (!firstNameText.value?.trim()) {
    errors.value.firstName = t('validators.requiredField', { fieldName: t('columns.firstName') })
  } else {
    const nameRegex = /^[\p{L}\s'-]+$/u
    if (!nameRegex.test(firstNameText.value)) {
      errors.value.firstName = t('validators.nameField', { fieldName: t('columns.firstName') })
    } else if (firstNameText.value.trim().length < 2) {
      errors.value.firstName = t('validators.fieldMinLength', { fieldName: t('columns.firstName'), minimumLength: 2 })
    } else if (firstNameText.value.length > 50) {
      errors.value.firstName = t('validators.fieldLength', { fieldName: t('columns.firstName'), maximumLength: 50 })
    } else {
      errors.value.firstName = ''
    }
  }
}

const validateLastName = () => {
  if (!lastNameText.value?.trim()) {
    errors.value.lastName = t('validators.requiredField', { fieldName: t('columns.lastName') })
  } else {
    const nameRegex = /^[\p{L}\s'-]+$/u
    if (!nameRegex.test(lastNameText.value)) {
      errors.value.lastName = t('validators.nameField', { fieldName: t('columns.lastName') })
    } else if (lastNameText.value.trim().length < 2) {
      errors.value.lastName = t('validators.fieldMinLength', { fieldName: t('columns.lastName'), minimumLength: 2 })
    } else if (lastNameText.value.length > 50) {
      errors.value.lastName = t('validators.fieldLength', { fieldName: t('columns.lastName'), maximumLength: 50 })
    } else {
      errors.value.lastName = ''
    }
  }
}

const validateEmailAddress = () => {
  if (!emailText.value?.trim()) {
    errors.value.emailAddress = t('validators.requiredField', { fieldName: t('columns.emailAddress') })
  } else if (!validateEmail(emailText.value)) {
    errors.value.emailAddress = t('validators.invalidEmail')
  } else {
    errors.value.emailAddress = ''
  }
}

function validatePassword() {
  errors.value.password = getFirstValidationMessage(passwordValidator, passwordText.value)
}

function validateConfirmPassword() {
  errors.value.confirmPassword = getFirstValidationMessage(confirmPasswordValidator, confirmPasswordText.value)
}

function getFirstValidationMessage(validators: unknown[], value: string): string {
  for (const validator of validators) {
    const validate = validator as (value: string) => string | undefined
    const message = validate(value)
    if (message) {
      return message
    }
  }
  return ''
}

function hasStepOneValidationErrors(): boolean {
  return Boolean(
    errors.value.firstName ||
    errors.value.lastName ||
    errors.value.emailAddress ||
    (!isAuthenticatedUser.value && errors.value.password) ||
    (!isAuthenticatedUser.value && errors.value.confirmPassword) ||
    birthDateError.value
  )
}

const emailValidator = [
  (value: string) => {
    if (!value || !value.trim()) {
      return t('validators.emailRequired');
    }
    return undefined;
  },
  createEmailFieldValidator(t, 'emailAddress'),
  consultantEmailValidator
]
const passwordValidator = [
  (value: string) => {
    if (!value || !value.trim()) {
      return t('validators.passwordRequired');
    }
    return undefined;
  },
  createPasswordFormatValidator(t, 'password')
]
const confirmPasswordValidator = [
  (value: string) => {
    if (!value || !value.trim()) {
      return t('validators.confirmPasswordRequired');
    }
    return undefined;
  },
  (value: string) => {
    return value === passwordText.value ? undefined : t('validators.passwordMismatch')
  }
]
// const addressLine1Validator = [createRequiredFieldValidator(t, 'addressLine1')]
// const cityValidator = [createRequiredFieldValidator(t, 'city')]
// const stateValidator = [createRequiredFieldValidator(t, 'stateCode')]
// const postalCodeValidator = [createRequiredFieldValidator(t, 'postalCode')]

</script>

<template>
  <div class="aoe-checkout container">
    <p class="pageHeader">{{ t("checkout.KitCheckout") }}</p>
    <p class="pageSubHeader">
      {{ t("checkout.CheckoutSubHeader") }}
    </p>

    <div class="row">
      <div class="col-8">
        <div id="BasicInfoDiv" class="checkOutSection">
          <kForm
            :initialValues="formInitialValues"
            :key="JSON.stringify(formInitialValues)"
            @submitclick="onSubmitClickHandler($event)"
            id="myForm"
            ref="myForm"
          >
            <kformelement>
              <div class="row">
                <div class="col-9 checkoutSectionHdr">{{ t("checkout.CheckoutYourInformationHdrStep") }}</div>
                <div v-if="step != 1" class="col-3 editBtn">
                  <button id="step1Edit" @click="editStep1ClickHandler">
                    {{ t("checkout.CheckoutEditStep") }}
                  </button>
                </div>
              </div>
              <div v-if="step != 1" class="row mt-3">
                <div class="col-12">
                  {{ firstNameText }}&nbsp;{{ lastNameText }}
                </div>
                <div class="col-12">
                  {{ emailText }}
                </div>
                <div class="col-12">
                  {{ phoneNumberText }}
                </div>
                <div v-if="!alreadyOptedin && (cart?.beeNumberCustomer || step != 1)">
                    <KCheckBox
                        ref="recieveEmailsCheckBox"
                        :label="t('labels.EmailMoreInfoCheckbox')"
                        v-model="marketingOptIn"
                      ></KCheckBox></div>
              </div>
              <div v-if="step == 1" class="stepInfo stepOne">
                <fieldset class="k-form-fieldset">
                  <div class="row">
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.firstName') }}</label>
                        <input
                          v-model="firstNameText"
                          type="text"
                          class="form-input"
                          :class="{ 'error': errors.firstName }"
                          required
                          @input="validateFirstName"
                          @blur="validateFirstName"
                        />
                        <div v-if="errors.firstName" class="error-message">{{ errors.firstName }}</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.lastName') }}</label>
                        <input
                          v-model="lastNameText"
                          type="text"
                          class="form-input"
                          :class="{ 'error': errors.lastName }"
                          required
                          @input="validateLastName"
                          @blur="validateLastName"
                        />
                        <div v-if="errors.lastName" class="error-message">{{ errors.lastName }}</div>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.emailAddress') }}</label>
                        <input
                          v-model="emailText"
                          type="email"
                          class="form-input"
                          :class="{ 'error': errors.emailAddress }"
                          :disabled="disableEmail"
                          required
                          @input="validateEmailAddress"
                          @blur="validateEmailAddress"
                        />
                        <div v-if="errors.emailAddress" class="error-message">{{ errors.emailAddress }}</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.phone') }}</label>
                        <LocalizationProvider :language="getKendoLanguage(locale)">
                          <IntlProvider :locale="getKendoLocale(locale)">
                            <PhoneInputControl
                              ref="phoneControl"
                              v-model="phoneModel"
                              :required="true"
                              :showExtension="false"
                              :showPhoneType="false"
                              :placeholder="t('consultantCorner.contactDetails.PhonePlaceholder')"
                            />
                          </IntlProvider>
                        </LocalizationProvider>
                      </div>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <div class="form-field">
                        <label class="form-label required">{{ t('columns.birthDate') }}</label>
                        <LocalizationProvider :language="getKendoLanguage(locale)">
                          <IntlProvider :locale="getKendoLocale(locale)">
                            <KDatePicker
                              :value="birthDate"
                              :placeholder="t('columns.birthDatePlaceholder')"
                              :format="'dd/MM/yyyy'"
                              :format-placeholder="'day/month/year'"
                              :start="'decade'"
                              :depth="'year'"
                              :class="{ 'k-invalid': birthDateError }"
                              @change="birthDateChangeHandler($event)"
                            />
                          </IntlProvider>
                        </LocalizationProvider>
                        <div v-if="birthDateError" class="error-message">{{ birthDateError }}</div>
                      </div>
                    </div>
                    <div class="col-6">
                      <label class="k-label">{{ t('columns.country') }}</label>
                      <DropDownList
                        id="countryDDL"
                        :data-items="countries"
                        :value="selectedCountry"
                        :text-field="'text'"
                        :data-item-key="'id'"
                        @change="countryChangeHandler($event.target.value)"
                      />
                    </div>
                  </div>
                  <div v-if="!isAuthenticatedUser" class="row">
                    <div class="col-6">
                      <ApplicationField
                        columnName="password"
                        :addAsterisk="true"
                        :disabled="false"
                        :keyField="false"
                        :additionalProperties="{ type: 'password' }"
                        @change="handlePasswordChange($event)"
                      />
                      <div v-if="errors.password" class="error-message">{{ errors.password }}</div>
                    </div>
                    <div class="col-6">
                      <ApplicationField
                        columnName="confirmPassword"
                        :addAsterisk="true"
                        :disabled="false"
                        :keyField="false"
                        :additionalProperties="{ type: 'password' }"
                        @change="handleConfirmPasswordChange($event)"
                      />
                      <div v-if="errors.confirmPassword" class="error-message">{{ errors.confirmPassword }}</div>
                    </div>
                  </div>
                  <div v-if="!isAuthenticatedUser && showPasswordPattern" class="row password-pattern-div bg-light">
                    <div class="col-12" v-html="t('validators.passwordPattern')"></div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12 optin">
                      <KCheckBox
                        ref="recieveEmailsCheckBox"
                        :label="t('labels.EmailMoreInfoCheckbox')"
                        v-model="marketingOptIn"
                      ></KCheckBox>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <KButton
                        @click="handleContinue1Click"
                        class="btnContinue"
                        :rounded="'large'"
                        :theme-color="'checkoutContinue'"
                        >{{t('labels.CheckoutContinue')}}</KButton
                      >
                    </div>
                  </div>
                </fieldset>
              </div>
            </kformelement>
          </kForm>
        </div>
        <div id="ShippingInfoDiv" class="checkOutSection">
          <kForm
            @submitclick="onSubmitClickHandler($event)"
            :key="JSON.stringify(secondFormInitialValues)"
            :initial-values="secondFormInitialValues"
            ref="mySecondForm"
          >
            <kformelement>
              <div class="row">
                <div class="col-9 checkoutSectionHdr">{{ t("checkout.CheckoutShippingHdrStep") }}</div>
                <div v-if="step > 2" class="col-3 editBtn">
                  <button id="step2Edit" @click="editStep2ClickHandler">
                    {{ t("checkout.CheckoutEditStep") }}
                  </button>
                </div>
              </div>
              <div v-if="step > 2 || (step != 2 && cart?.shippingAddress != null)" class="row mt-3">
                <div v-if="countryText == 'US' || countryText == 'CA'">
                  <div class="col-12">
                    {{ addressFirstNameText }}&nbsp;{{ addressLastNameText }}
                  </div><div class="col-12">
                    {{ addressLine1Text }}
                  </div>
                  <div class="col-12">
                    {{ addressLine2Text }}
                  </div>
                  <div class="col-12">
                    {{ cityText }},&nbsp;{{ stateText }}&nbsp;{{ postalCodeText }}
                  </div>
                </div>
                <div v-if="countryText == 'AT' || countryText == 'DE' || countryText == 'FR'">
                  <div class="col-12">
                    {{ addressFirstNameText }}&nbsp;{{ addressLastNameText }}
                  </div>
                  <div class="col-12">
                    {{ addressLine1Text }}
                  </div>
                  <div class="col-12">
                    {{ houseNumberText }}
                  </div>
                  <div v-if="countryText != 'AT'" class="col-12">
                    {{ addressLine2Text }}
                  </div>
                  <div class="col-12">
                    {{ postalCodeText }}
                  </div>
                  <div class="col-12">
                    {{ cityText }}
                  </div>
                </div>
              </div>
              <div v-if="step == 2" class="mt-3 stepInfo stepTwo">
                <div class="col-12 checkoutSectionSubHdr">
                  {{ t("checkout.AddressRequirementMessage") }}
                </div>

                <fieldset class="k-form-fieldset">
                <AddressControl
                  ref="shippingAddressControl"
                  :countryCode="countryText"
                  :addressLine1="addressLine1Text"
                  :addressLine2="addressLine2Text"
                  :houseNumber="houseNumberText"
                  :city="cityText"
                  :stateCode="stateText"
                  :postalCode="postalCodeText"
                  :columnClass="'col-12'"
                  :showfullnamefield="true"
                  :hidecountrypicker="false"
                  :isdisabled="false"
                  :isrequired="true"
                  :name="addressNameDisplay"
                  @address-change="addressControlUpdateHandler">
                </AddressControl>
                  <!-- <div class="row">
                    <div class="col-6">
                      <ApplicationField
                        columnName="firstName"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                    <div class="col-6">
                      <ApplicationField
                        columnName="lastName"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                  </div><div class="row">
                    <div class="col-12">
                      <ApplicationField
                        columnName="addressLine1"
                        :validator="addressLine1Validator"
                        :addAsterisk="true"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <ApplicationField
                        columnName="addressLine2"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-12">
                      <ApplicationField
                        columnName="city"
                        :validator="cityValidator"
                        :addAsterisk="true"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                  </div>
                  <div class="row">
                    <div class="col-6">
                      <ApplicationField
                        columnName="stateCode"
                        :validator="stateValidator"
                        :addAsterisk="true"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                    <div class="col-6">
                      <ApplicationField
                        columnName="postalCode"
                        :validator="postalCodeValidator"
                        :addAsterisk="true"
                        :keyField="false"
                      ></ApplicationField>
                    </div>
                  </div> -->
                  <div class="row mt-2">
                    <div class="col-12">
                      <KCheckBox
                        ref="sameAsBillingCheckBox"
                        :label="t('labels.SameAsBillingCheckbox')"
                      ></KCheckBox>
                    </div>
                  </div>
                  <div class="row mt-2">
                    <div class="col-12">
                      <KButton
                        ref="btnContinue2"
                        id="btnContinue2"
                        class="btnContinue"
                        :rounded="'large'"
                        type="submit"
                        :theme-color="'checkoutContinue'"
                        >{{t('labels.CheckoutContinue')}}</KButton
                      >
                    </div>
                  </div>
                </fieldset>
              </div>
            </kformelement>
          </kForm>
        </div>
        <div id="PaymentInfoDiv" class="checkOutSection">
          <div class="row">
            <div class="col-9 checkoutSectionHdr">{{t('checkout.CheckoutPaymentHdrStep')}}</div>
            <div v-if="step > 3" class="col-3 editBtn">
              <button @click="step = 3">{{t('checkout.CheckoutEditStep')}}</button>
            </div>
          </div>
          <div v-if="step > 3" class="row">
            <div class="col-12">{{ t('labels.PaymentInfoPlaceholder') }}</div>
          </div>
          <div v-if="step == 3" class="stepInfo stepThree">
            <AoeCheckoutPayment
              v-if="cart != null"
              :cart="cart"
            ></AoeCheckoutPayment>
            <div class="row mt-2">
              <div class="col-12">
                <KButton
                  class="btnCheckout"
                  :rounded="'large'"
                  :theme-color="'primary'"
                  @click="placeOrderClickHandler"
                  :disabled="(cart?.balance != undefined && cart?.balance > 0) || certificationAccepted == false || ethicsAccepted == false"
                  >{{t('labels.BecomeAConsultant')}}</KButton
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-4">
        <div class="checkOutSection checkOutSectionOrderSummary">
          <div class="row">
            <div class="col-12 checkoutSectionHdr">{{t('checkout.CheckoutOrderSummaryHdr')}}</div>
          </div>
          <div class="row">
            <div class="col-12"></div>
          </div>
          <ExpansionPanel
            :title="orderSummaryItemTitle"
            :expanded="expanded"
            :expand-icon="'arrow-seek-down'"
            :collapse-icon="'arrow-seek-up'"
            @action="handleAction"
          >
            <Reveal :appear="expanded">
              <ExpansionPanelContent v-if="expanded">
                <div class="row">
                  <div class="col-12 checkoutItems">
                    <div
                      class="checkoutItems"
                      v-for="(cartItem, index) in cart?.items"
                      :key="cartItem.lineNumber"
                    >
                      <div class="row mt-2 mobileCartItems">
                        <div class="product-image col-3">
                          <a
                            :href="
                              GetFullProductUrlFromStringDictionary(
                                cartItem.productCode,
                              )
                            "
                            :data-category-code="cartItem.productCode"
                          >
                            <img
                              :src="
                                GetFullProductImageUrlFromStringDictionary(
                                  cartItem.productCode,
                                )
                              "
                              class="img-fluid prodImage"
                              :alt="t('cart.ProductImageAlt')"
                            />
                          </a>
                        </div>
                        <div class="col-9 cartItemInfoMobile">
                          <div class="row">
                            <div class="col-10">
                              <div class="cartSkuName">
                                {{ cartItem.skuName }}
                              </div>
                              <div class="cartSkuCode">
                                #{{ cartItem.skuCode }}
                              </div>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col-4">
                              <div class="number">
                                {{ t('checkout.Quantity') }}: {{ cartItem.quantity }}
                              </div>
                            </div>
                            <div class="col-8">
                              <div class="cartItemPrice">
                                {{ FormatCurrency((cartItem.price * cartItem.quantity) - (cartItem.totalDiscountAmount != null ? cartItem.totalDiscountAmount : 0), cart?.currencyId || null) }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div  v-if="cart?.items.length != undefined ? index < cart?.items.length - 1 : false" class="row mt-2">
                          <div class="col-12">
                            <hr />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ExpansionPanelContent>
            </Reveal>
          </ExpansionPanel>

          <div class="row mt-3">
            <div class="col-6 lblTotalRegularPrice">{{t('checkout.CheckoutTotalRegularPrice')}}</div>
            <div class="col-6 lblTotalRegularPriceValue">
              {{ FormatCurrency(GetCartProductSubtotal(cart), cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-6 lblSubtotal">{{t('checkout.CheckoutSubtotal')}}</div>
            <div class="col-6 lblSubtotalValue">
              {{ FormatCurrency(cart?.subtotal || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-6 shipping-label">{{t('checkout.CheckoutShipping')}}</div>
            <div class="col-6 shipping-value">
              {{ FormatCurrency(cart?.shippingCost || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row">
            <div class="col-7 lblTax">{{t('checkout.CheckoutTax')}}</div>
            <div class="col-5 lblTaxValue text-end">
              <span class="pe-3">-</span>
            </div>
            <div class="col-6 lblTotal">{{t('checkout.CheckoutTotal')}}</div>
            <div class="col-6 lblTotalValue">
              {{ FormatCurrency(cart?.total || 0, cart?.currencyId || null) }}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12">
              <div class="certification-checkbox">
                <KCheckBox
                  v-model="certificationAccepted"
                  class="me-2"
                />
                <span class="certification-text">
                  <span v-if="iceTermsVersionData?.shortTermsAckText" v-html="iceTermsVersionData.shortTermsAckText"></span>
                  <i18n-t v-else keypath="terms.certification" tag="span">
                    <template #consultantAgreementLink>
                      <a :href="consultantAgreementUrl" target="_blank">{{t('terms.independentConsultantAgreement')}}</a>
                    </template>
                  </i18n-t>
                </span>
              </div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12">
              <div class="certification-checkbox">
                <KCheckBox
                  v-model="ethicsAccepted"
                  class="me-2"
                />
                <span class="certification-text">
                  <span v-if="iceTermsVersionData?.terms" v-html="iceTermsVersionData.terms"></span>
                  <i18n-t v-else keypath="terms.ethics" tag="span">
                    <template #prohibitedBusinessPracticesLink>
                      <a :href="prohibitedPracticesUrl" target="_blank">{{t('terms.prohibitedBusinessPractices')}}</a>
                    </template>
                    <template #codeOfEthicsLink>
                      <a :href="codeOfConductUrl" target="_blank">{{t('terms.codeOfEthics')}}</a>
                    </template>
                  </i18n-t>
                </span>
              </div>
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 text-center terms-agreement">
              <i18n-t keypath="terms.agreement" tag="p">
                <template #termsLink>
                  <a :href="termsUrl" target="_blank">{{t('terms.termsOfUse')}}</a>
                </template>
                <template #privacyLink>
                  <a :href="privacyPolicyUrl" target="_blank">{{t('terms.privacyPolicy')}}</a>
                </template>
              </i18n-t>
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <KButton
                class="btnCheckout"
                :rounded="'large'"
                :theme-color="'primary'"
                @click="placeOrderClickHandler"
                :disabled="(cart?.balance != undefined && cart?.balance > 0) || certificationAccepted == false || ethicsAccepted == false"
                >{{t('labels.BecomeAConsultant')}}</KButton
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Popup
    :anchor="'btnContinue2'"
    :show="show"
    :popup-class="'aoe-address-val-popup-content'"
  >
    <VerifyAddressPopup
      :enteredAddress="{
        addressLine1: addressLine1Text,
        addressLine2: addressLine2Text,
        houseNumber: houseNumberText,
        city: cityText,
        stateCode: stateText,
        postalCode: postalCodeText,
        countryCode: countryText,
        firstName: addressFirstNameText,
        lastName: addressLastNameText,
      }"
      :verifyAddress="verifyAddress"
      :verifyAddressError="addressVerifyError"
      @use-entered-address="useEnteredAddressClick"
      @use-selected-address="useSelectedAddressClick"
      @re-enter-address="show = false">
    </VerifyAddressPopup>
  </Popup>


  <div class="kit-checkout-overlays">
  <!-- SKU Selection Dialog -->
  <Dialog
    v-if="showSkuSelectionDialog"
    :title="t('kit.selectKitTitle')"
    @close="closeSkuSelectionDialog"
    :width="800"
  >
    <div class="sku-selection-dialog">
      <p class="dialog-message">{{ t('kit.selectKitMessage') }}</p>
      <div class="kits-container">
        <div v-for="product in availableKitProducts" :key="product.productCode" class="kit-card">
          <div class="kit-image">
            <img
              v-if="productImageUrls[product.productCode]"
              :src="productImageUrls[product.productCode]"
              :alt="product.productName"
            />
          </div>
          <div class="kit-details">
            <h3 class="kit-name">{{ product.productName }}</h3>
            <div class="kit-sku" v-if="product.skus && product.skus.length > 0">
              <div class="sku-price">
                {{ FormatCurrency(product.skus[0].retailPrice, cart?.currencyId || null) }}
                <span class="total-value" v-if="product.attributes?.TotalValue">
                  {{ t('kit.totalValue', [product.attributes.TotalValue]) }}
                </span>
              </div>
              <div class="sku-stock" :class="getStockClass(product.skus[0].inventoryLevel)">
                {{ getInventoryLevelText(product.skus[0].inventoryLevel.inventoryLevel) }}
              </div>
            </div>
            <div class="kit-actions">
              <KButton
                v-if="product.skus && product.skus.length > 0"
                :theme-color="'primary'"
                @click="handleSkuSelection(product.skus[0].skuCode)"
                :disabled="product.skus[0].inventoryLevel?.inventoryLevel === 'OutOfStock' && !product.skus[0].outOfStockVisible"
              >
                {{ t('kit.selectKit') }}
              </KButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <DialogActionsBar>
      <KButton @click="closeSkuSelectionDialog">{{ t('buttons.Cancel') }}</KButton>
    </DialogActionsBar>
  </Dialog>
  </div>
</template>

<style lang="scss">

.stepTwo .k-input-solid.k-invalid
{
  border-color: var(--kendo-color-border, black) !important;
}

.aoe-address-val-popup-content {
    position: fixed;
    top: 50%;
    left: 50%;
    padding: $card-padding;
    transform: translate(-50%, -50%);
    z-index: $z-critical;
}


.divUseAddress
{
  text-align: right;
}

.enteredAddress
{
  margin-left: 30px;
}

.verifyaddressLine
{
  margin-left: 30px;
}

.k-form .k-form-fieldset {
  margin-top: 10px;
}

.password-pattern-div {
  border-radius: $radius-md;
  padding: $spacing-2-5;
  margin: $spacing-2-5;
}

.terms-agreement {
  @include typography-body;
  color: #666;
}

.terms-agreement a {
  color: #0066cc;
  text-decoration: none;
}

.terms-agreement a:hover {
  text-decoration: underline;
}
.certification-checkbox {
  display: flex;
  align-items: flex-start;
  @include typography-body;
  margin-bottom: 15px;
}

.certification-checkbox .k-checkbox {
  animation: pulse-shadow 2s infinite;
}

@keyframes pulse-shadow {
  0% {
    box-shadow: 0 0 0 0 rgba(#0066cc, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(#0066cc, 0.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(#0066cc, 0);
  }
}

.certification-text {
  line-height: 1.4;
  margin-left: 8px;
}

/* SKU Selection Dialog Styles */
.sku-selection-dialog {
  padding: $spacing-4;

  .dialog-message {
    @include typography-body;
    margin-bottom: $spacing-6;
    color: var(--color-text);
  }

  .kits-container {
    display: flex;
    flex-direction: column;
    gap: $spacing-4;
    max-height: 500px;
    overflow-y: auto;
  }

  .kit-card {
    display: flex;
    gap: $spacing-4;
    padding: $card-padding;
    border: 1px solid var(--color-border);
    border-radius: $card-radius;
    background: var(--color-background);
    transition: box-shadow 0.2s ease;

    &:hover {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }
  }

  .kit-image {
    flex-shrink: 0;
    width: 120px;
    height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f8f9fa;
    border-radius: $radius-md;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .kit-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: $spacing-2;
  }

  .kit-name {
    @include typography-h4;
    margin: 0;
    color: var(--color-heading);
  }

  .kit-sku {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $spacing-3;
  }

  .sku-price {
    @include typography-body-large;
    font-weight: 600;
    color: var(--color-text);

    .total-value {
      @include typography-small;
      font-weight: 400;
      color: #666;
      margin-left: $spacing-2;
    }
  }

  .sku-stock {
    @include typography-small;
    padding: $spacing-1 $spacing-3;
    border-radius: $badge-radius;
    font-weight: 500;

    &.stock-available {
      background: #d4edda;
      color: #155724;
    }

    &.stock-low {
      background: #fff3cd;
      color: #856404;
    }

    &.stock-out {
      background: #f8d7da;
      color: #721c24;
    }
  }

  .kit-actions {
    margin-top: auto;

    .k-button {
      min-width: 150px;
    }
  }
}

.btnContinue {
    /* !important required: Override Kendo button defaults for custom checkout continue styling */
    color: white !important;
    background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
    display: flex;
    height: 48px;
    padding: $spacing-4 56px;
    justify-content: center;
    align-items: center;
    gap: $gap-sm;
    align-self: stretch;
    border-radius: $radius-md;
    border: none;
    text-align: center;
    @include typography-body;
    @include font-weight-semibold;
    font-style: normal;
    text-transform: uppercase;
    margin-top: $spacing-5;
}

.btnContinue:hover:not(:disabled) {
    background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
    filter: brightness(0.85);
}

// Custom form field styling to match PhoneInputControl
.form-field {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: $spacing-3;
}

.form-label {
  @include typography-body;
  @include font-weight-medium;
  color: #333;
  margin-bottom: 0.5rem;
}

.form-label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-input {
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  height: 48px;
}

.form-input:focus {
  outline: none;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
  border-color: #007bff;
}

.form-input.error {
  border-color: #dc3545;
}

.form-input.error:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 0.2rem rgba(#dc2626, 0.25);
}

.error-message {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
}

// Override PhoneInputControl to match custom form styling
.phone-input-control .country-select {
  border-color: #ced4da;
  height: 48px;
}

.phone-input-control .country-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

.phone-input-control .k-textbox {
  border-color: #ced4da;
  height: 48px;
}

.phone-input-control .k-textbox:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(#007bff, 0.3);
}

// Mobile responsive adjustments
@media (max-width: 576px) {
  .stepOne .row {
    margin-bottom: $spacing-2;
  }

  .form-field {
    margin-bottom: $spacing-2;
  }
}
</style>
